import { useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";

// ── Field definitions mirroring Blazor CopyFieldOptionsByRole ────────────────

interface FieldOption {
  fieldName: string;
  displayName: string;
  isSelected: boolean;
  adminOnly?: boolean;
}

const ALL_FIELDS: FieldOption[] = [
  { fieldName: "priorID",           displayName: "Prior ID",               isSelected: true, adminOnly: true },
  { fieldName: "status",            displayName: "Tier",                   isSelected: true, adminOnly: true },
  { fieldName: "meetingName",       displayName: "Meeting Name",           isSelected: true },
  { fieldName: "business",          displayName: "Business",               isSelected: true },
  { fieldName: "meetingType",       displayName: "Meeting Type",           isSelected: true },
  { fieldName: "businessLine",      displayName: "Business Line",          isSelected: true },
  { fieldName: "meetingCategory",   displayName: "Meeting Category",       isSelected: true },
  { fieldName: "attendeeLevel",     displayName: "Attendee Level",         isSelected: true },
  { fieldName: "location",          displayName: "Location",               isSelected: true },
  { fieldName: "programOwner",      displayName: "CLO or Sponsoring PPMD", isSelected: true },
  { fieldName: "programContact",    displayName: "Program Contact",        isSelected: true },
  { fieldName: "businessAdvisor",   displayName: "Business Advisor",       isSelected: true },
  { fieldName: "programManagerName", displayName: "Program Manager",      isSelected: true },
  { fieldName: "draftPreferredDates", displayName: "Dates & Room Blocks",  isSelected: true },
];

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CopyToNewDialogProps {
  /** Whether the current user is Admin or Scheduler — shows extra fields (Prior ID, Tier) */
  isAdminOrScheduler?: boolean;
  /** Called with the set of field names the user chose to copy (the selected ones) */
  onCopy: (selectedFields: Set<string>) => void;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CopyToNewDialog({ isAdminOrScheduler = false, onCopy, onClose }: CopyToNewDialogProps) {
  const visibleFields = ALL_FIELDS.filter((f) => isAdminOrScheduler || !f.adminOnly);

  const [fields, setFields] = useState<FieldOption[]>(
    visibleFields.map((f) => ({ ...f }))
  );

  const allSelected = fields.every((f) => f.isSelected);
  const noneSelected = fields.every((f) => !f.isSelected);

  function toggleSelectAll() {
    const next = !allSelected;
    setFields((prev) => prev.map((f) => ({ ...f, isSelected: next })));
  }

  function toggleField(fieldName: string, value: boolean) {
    setFields((prev) => prev.map((f) => f.fieldName === fieldName ? { ...f, isSelected: value } : f));
  }

  function handleCopy() {
    const selected = new Set(fields.filter((f) => f.isSelected).map((f) => f.fieldName));
    if (selected.size === 0) {
      toast.error("Please select at least one field to copy.");
      return;
    }
    onCopy(selected);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-to-new-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-full max-w-[720px] border border-slate-300 bg-white shadow-xl flex flex-col"
          style={{ maxHeight: "min(90vh, 485px)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-black px-4 py-3 flex-shrink-0">
            <h2 id="copy-to-new-title" className="text-base font-semibold text-white">
              Copy to New Meeting
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-white hover:text-slate-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Section heading + Select All toggle */}
            <div className="mb-3 mt-1 flex items-center gap-3 font-semibold" style={{ color: "#63666A" }}>
              Copy Fields
              <button
                type="button"
                className="font-normal"
                style={{ color: "#007CB0" }}
                onClick={toggleSelectAll}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>

            {/* Field checkboxes — 2-column grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {fields.map((field) => (
                <div key={field.fieldName} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`copy-field-${field.fieldName}`}
                    checked={field.isSelected}
                    onChange={(e) => toggleField(field.fieldName, e.target.checked)}
                    className="h-4 w-4 accent-black"
                  />
                  <label
                    htmlFor={`copy-field-${field.fieldName}`}
                    className="text-sm"
                    style={{ color: "#000" }}
                  >
                    {field.displayName}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-slate-200 px-4 py-3">
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCopy}
                disabled={noneSelected}
                className="rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
